import * as Sentry from "@sentry/react";
import cx from "classnames";
import React, { useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Input from "../../common/components/Forms/Input/Input";
import { getFeedback, getReferBonusTerms, sendFeedback, setCurrentPageProgress } from "../api/ApiAppForm";
import AFLayout from "../components/AFLayout";
import AFNavigation from "../components/AFNavigation";
import AFRateExperience, { IRatings } from "../components/AFRateExperience";
import AFSecure from "../components/AFSecure";
import AFReferBonusTCDialog from "../components/Modals/AFReferBonusTCDialog";
import { PageType } from "../enums/PageType";
import useAFForm from "../hooks/useAFForm";
import { IPageInfo } from "../interfaces/IAppFormData";
import { AppFormControllerContext } from "../providers/AppFormProvider";
import styles from "../scss/AFSubmit.module.scss";

interface IAFSubmitProps {
    applicationId: string;
}

const AFSubmitPage: React.FC = () => {
    const history = useHistory();
    const params = useParams<IAFSubmitProps>();
    const applicationId = params.applicationId;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [isCodeValid, setIsCodeValid] = useState(false);
    const [referralCode, setReferralCode] = useState("");
    const [experience, setExperience] = useState<IRatings>();
    const [defaultExperience, setDefaultExperience] = useState<IRatings>();
    const [termsContent, setTermsContent] = useState("");
    const [isOpenTerms, setIsOpenTerms] = useState(false);
    const [isInErrorState, setIsInErrorState] = useState(false);

    const { selectors } = useContext(AppFormControllerContext);
    const { checkValidApplication } = useAFForm();

    const handleNextPage = (): void => {
        if ((isCodeValid && referralCode) || experience?.experienceRating || experience) {
            setIsSubmitting(true);
            sendFeedback(parseInt(applicationId), selectors.getQuoteGroupId(), {
                applicationId: parseInt(applicationId),
                referralCode: referralCode || "",
                experienceRating: parseInt(experience?.experienceRating) || 0,
                experienceComments: experience?.experienceComments || ""
            })
                .then((res) => {
                    if (!res) return;
                    const pageInfo: IPageInfo = {
                        pageType: PageType.Done,
                        currentClientNumber: 1
                    }
                    setCurrentPageProgress(selectors.getApplicationId(), selectors.getQuoteGroupId(), pageInfo)
                        .then(() => {
                            setIsSubmitting(false);
                            history.push(`/app-form/done/${applicationId}`);
                        })
                        .catch(() => {
                            setIsInErrorState(true);
                        });
                })
                .catch(() => {
                    setIsInErrorState(true);
                });
            return;
        }
        const pageInfo: IPageInfo = {
            pageType: PageType.Done,
            currentClientNumber: 1
        };
        setCurrentPageProgress(selectors.getApplicationId(), selectors.getQuoteGroupId(), pageInfo)
            .then(() => {
                history.push(`/app-form/done/${applicationId}`);
            })
            .catch(() => {
                setIsInErrorState(true);
            });
    };

    const handleBack = (e): void => {
        e.preventDefault();
        setIsReady(false);
        const applicantList = selectors.getApplicantList();
        const lastApplicant = applicantList.reduce((prev, current) => (prev && prev.clientNumber > current.clientNumber) ? prev : current);
        history.push(`/app-form/submit/${applicationId}/applicant${lastApplicant.clientNumber}/signature`);
    };

    const handleReferralCode = (value) => {
        if (value?.length > 5) return;
        setReferralCode(value);
        if (!value) {
            setIsCodeValid(false);
            return;
        }
        setIsCodeValid(value?.length === 5);
    };

    const handleRating = (ratings: IRatings): void => {
        setExperience(ratings);
    };

    const handleGetFeedback = (): void => {
        getFeedback(parseInt(applicationId), selectors.getQuoteGroupId()).then((res) => {
            if (!res?.applicationId) return;
            handleReferralCode(res?.referralCode);
            setDefaultExperience({
                experienceRating: res?.experienceRating
                    ? String(res?.experienceRating)
                    : "",
                experienceComments: res?.experienceComments
            });
        });
    };

    const handleGetTerms = (): void => {
        getReferBonusTerms().then((res) => {
            setTermsContent(res || "");
        });
    };

    useEffect(() => {
        if (!applicationId) return;
        checkValidApplication(applicationId, selectors);
        handleGetFeedback();
        handleGetTerms();
    }, [applicationId]);

    useEffect(() => {
        const keyDownHandler = (event) => {
            if (event.key === "Escape") {
                event.preventDefault();
                handleBack(event);
            }
        };

        document.addEventListener("keydown", keyDownHandler);
        return () => {
            document.removeEventListener("keydown", keyDownHandler);
        };
    }, []);

    useEffect(() => {
        if (typeof Sentry !== 'undefined' && isInErrorState) {
            Sentry.captureMessage(`Error occured for ApplicationId ${applicationId} on the Submit page.`, "error");
        }
    }, [isInErrorState]);

    return (
        <AFLayout isInErrorState={isInErrorState} currentPage={PageType.Submit}>
            <AFReferBonusTCDialog
                isOpen={isOpenTerms}
                onClose={() => {
                    setIsOpenTerms(false);
                }}
                content={termsContent}
            />
            <div className={cx(styles["af-submit-page"], styles["layout"])}>
                <div className={styles["back-btn-wrapper"]}>
                    <AFSecure />
                </div>

                <div
                    className={cx(
                        styles["layout-flex-column--centered"],
                        styles["af-submit-container"],
                        styles["ready-submit"]
                    )}>
                    <div className={cx(styles["submit-title-label"])}>
                        Ready to Submit
                    </div>

                    <p className={styles["af-payment-info"]}>
                        Hit "Submit" below and your application will be underway!
                    </p>

                    <p className={styles["af-payment-info"]}>
                        <strong>NOTE: </strong>If you have skipped questions, our team will be in touch to help you finish them. You will then need to sign a new declaration form.
                    </p>

                    <div className={styles["af-bank-signature"]}>
                        <label
                            className={styles["af-bank-signature--label"]}
                            htmlFor="Do you have a referral code? Enter it here:">
                            Do you have a referral code? Enter it here:
                        </label>
                        <p className={styles["af-payment-info"]}>
                            When your policy goes in force, both you and your referrer
                            will get $50
                        </p>
                        <Input
                            id="Do you have a referral code? Enter it here:"
                            name="Do you have a referral code? Enter it here:"
                            type="text"
                            value={referralCode}
                            onChange={(e) => {
                                handleReferralCode(e.target.value);
                            }}
                            placeholder="XXXXX"
                        />
                        <div className={styles["af-bank-signature--span"]}>
                            By using a referral code, you agree to our{" "}
                            <a onClick={() => setIsOpenTerms(true)}>T&Cs.</a>
                        </div>
                    </div>

                    <AFRateExperience
                        data={defaultExperience}
                        ratingChanges={handleRating}
                    />
                    <AFNavigation
                        handleBack={handleBack}
                        skipIsVisible={false}
                        forwardIsVisible={true}
                        forwardIsSubmitting={isSubmitting}
                        forwardIsDisabled={isSubmitting || (referralCode && !isCodeValid)}
                        forwardType="button"
                        onClick={handleNextPage}
                        forwardText={"Submit"}
                        className={"submit-nav"}
                    />
                </div>
            </div>
        </AFLayout>
    );
};

export default AFSubmitPage;
